import axios from "axios";
import { AUTH_KEYS } from "./authKeys";
import { writeLogoutExpiry } from "./sessionExpiry";

export interface SessionRefreshResult {
  ok: boolean;
  status?: number;
  error?: string;
}

export interface SessionRefreshPayload {
  status: number;
  sessionToken?: string;
  sessionExpiresInMs?: number;
  tokens?: {
    Authorization?: string;
    AuthorizationRA?: string;
    AuthorizationRCB?: string;
    AuthorizationForce?: string;
  };
  error?: string;
}

function apiBaseUrl(): string {
  return import.meta.env.MODE === "production"
    ? (import.meta.env.VITE_BASE_URL_PROD as string)
    : (import.meta.env.VITE_BASE_URL_DEV as string);
}

let refreshInFlight: Promise<SessionRefreshResult> | null = null;

export async function silentRefreshSession(): Promise<SessionRefreshResult> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const sessionToken = localStorage.getItem(AUTH_KEYS.SESSION_TOKEN)?.trim();
    if (!sessionToken) {
      return { ok: false, status: 401, error: "Sem session token." };
    }

    try {
      const { data } = await axios.post<SessionRefreshPayload>(
        `${apiBaseUrl()}/session/refresh`,
        {},
        {
          headers: {
            "X-Session-Token": sessionToken,
            Accept: "*/*",
          },
        }
      );

      if (!data || data.status !== 200 || !data.tokens) {
        return {
          ok: false,
          status: data?.status ?? 500,
          error: data?.error ?? "Falha ao renovar sessão.",
        };
      }

      if (data.sessionToken) {
        localStorage.setItem(AUTH_KEYS.SESSION_TOKEN, data.sessionToken);
      }

      localStorage.setItem(AUTH_KEYS.AUTHORIZATION, data.tokens.Authorization ?? "");
      localStorage.setItem(AUTH_KEYS.AUTHORIZATION_RA, data.tokens.AuthorizationRA ?? "");
      localStorage.setItem(AUTH_KEYS.AUTHORIZATION_FORCE, data.tokens.AuthorizationForce ?? "");

      const inForce = localStorage.getItem(AUTH_KEYS.IN_FORCE) === "true";
      const fallbackMs = inForce ? 60 * 60 * 1000 : 5 * 60 * 60 * 1000;
      writeLogoutExpiry(data.sessionExpiresInMs ?? fallbackMs);

      const sessionRaw = localStorage.getItem(AUTH_KEYS.SESSION);
      if (sessionRaw) {
        try {
          const sessionUser = JSON.parse(sessionRaw) as { loginTimestamp?: number };
          sessionUser.loginTimestamp = Date.now();
          localStorage.setItem(AUTH_KEYS.SESSION, JSON.stringify(sessionUser));
        } catch {
          /* mantém sessão anterior */
        }
      }

      return { ok: true, status: 200 };
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      return {
        ok: false,
        status: ax.response?.status ?? 500,
        error: ax.response?.data?.error ?? "Falha ao renovar sessão.",
      };
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}
